import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { makeStyles } from "@material-ui/core/styles";
import * as appActions from "../../actions/appActions";
import { athenaURL } from "../../env";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "auto",
    padding: theme.spacing(4),
    textAlign: "center",
    fontFamily: "Ride",
  },
  spinner: {
    marginBottom: theme.spacing(3),
  },
  successIcon: {
    fontSize: 60,
    color: "green",
    marginBottom: theme.spacing(2),
  },
  errorIcon: {
    fontSize: 60,
    color: "red",
    marginBottom: theme.spacing(2),
  },
  message: {
    marginBottom: theme.spacing(2),
    fontSize: 18,
    fontWeight: "bold",
  },
  subMessage: {
    color: "#666",
    marginBottom: theme.spacing(4),
  },
  button: {
    marginTop: theme.spacing(2),
    width: "200px",
  },
}));

const DiveCompletePage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [errorMessage, setErrorMessage] = useState("");

  const authorization = useSelector((state) => state.vir.athenaAuth.authorization);
  const virId = searchParams.get("vir");
  const category = searchParams.get("category");
  const requestId = searchParams.get("requestId");
  console.log("reqId:", requestId);

  useEffect(() => {
    const createDiveRecord = async () => {
      if (!virId || !category || !authorization) {
        console.error("[DiveComplete] Missing required parameters:", {
          virId,
          category,
          authorization: !!authorization,
        });
        setStatus("error");
        setErrorMessage("Missing required information. Please try again.");
        return;
      }

      try {
        console.log(
          `[DiveComplete] Creating DIVE record for VIR ID: ${virId}, Category: ${category}`
        );

        const url = `${athenaURL}/vir/dive/create/${virId}/${category}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authorization}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log("[DiveComplete] Response:", data);

        if (data?.data || response.ok) {
          console.log("[DiveComplete] DIVE record created successfully");
          setStatus("success");
          dispatch(
            appActions.createToast({
              type: "success",
              message: "Driver license verification completed successfully",
            })
          );
        } else {
          throw new Error(data?.message || "Failed to create DIVE record");
        }

        await fetch(`${athenaURL}/vir/dive/save`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authorization}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            virId: virId,
            category: category,
            requestId: requestId,
          }),
        });
      } catch (err) {
        console.error("[DiveComplete] Error creating DIVE record:", err);
        setStatus("error");
        setErrorMessage(err?.message || "Failed to process verification. Please try again.");
        dispatch(
          appActions.createToast({
            type: "error",
            message: "Failed to complete driver license verification",
          })
        );
      }
    };

    createDiveRecord();
  }, [virId, category, authorization, dispatch]);

  return (
    <Box className={classes.container}>
      {status === "processing" && (
        <>
          <CircularProgress size={60} className={classes.spinner} />
          <Typography className={classes.message}>Processing verification...</Typography>
          <Typography className={classes.subMessage}>
            Please wait while we complete your driver license verification.
          </Typography>
        </>
      )}

      {status === "success" && (
        <>
          <CheckCircleOutlineIcon className={classes.successIcon} />
          <Typography className={classes.message}>Verification Complete!</Typography>
          <Typography className={classes.subMessage}>
            Please close the window, go back to continue
          </Typography>
        </>
      )}

      {status === "error" && (
        <>
          <ErrorOutlineIcon className={classes.errorIcon} />
          <Typography className={classes.message}>Verification Failed</Typography>
          <Typography className={classes.subMessage}>{errorMessage}</Typography>
        </>
      )}
    </Box>
  );
};

export default DiveCompletePage;
